<template>
  <div class="p-company-edit-form">
    <div class="p-company-edit-form__content el-bg--brand-lightest px-24 pt-24">
      <company-form
        ref="form"
        v-model="formValue"
        is-extended
        @status="handleFormStatusChange"
      />
    </div>
    <div class="p-divider" />
    <div class="p-company-edit-form__footer px-24 pb-16">
      <el-button
        type="secondary"
        data-test-id="company-edit-form.button-cancel"
        @click="handleCancelClick"
      >
        {{ $t('action.cancel') }}
      </el-button>
      <el-button
        native-type="submit"
        :loading="isCompanyEditing"
        :disabled="isSaveDisabled"
        data-test-id="company-edit-form.button-save"
        @click="handleSaveClick"
      >
        {{ $t('action.save') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { setAutoFocus } from '@/app/library/setAutoFocus';
import CompanyForm from '@/app/components/CompanyForm';
import { areCompanyRequiredFieldsFilled, getDefaultCompanyFormValue } from '@/app/domain/Company';
import { areContactRequiredFieldsFilled, isCompanyBillingContact, isLegalContact } from '@/app/domain/Contact';

export default {
  name: 'CompanyEditForm',
  components: {
    CompanyForm
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    tenantContacts: {
      type: Array,
      default: () => []
    }
  },
  emits: [
    'submit',
    'cancel'
  ],
  data() {
    return {
      formValue: getDefaultCompanyFormValue(),
      formStatus: {}
    };
  },
  computed: {
    ...mapGetters('app', [
      'isCompanyEditing'
    ]),
    isSaveDisabled() {
      return !areCompanyRequiredFieldsFilled(this.formValue)
        || !areContactRequiredFieldsFilled(this.formValue.legalContact, true)
        || !areContactRequiredFieldsFilled(this.formValue.billingContact, true)
        || this.isCompanyEditing
        || this.formStatus.invalid;
    }
  },
  created() {
    const legalContact = this.tenantContacts.find((contact) => isLegalContact(contact));
    const billingContact = this.tenantContacts.find((contact) => isCompanyBillingContact(contact));

    this.formValue = {
      ...this.formValue,
      ...this.value,
      legalContact: {
        ...this.formValue.legalContact,
        ...legalContact
      },
      billingContact: {
        ...this.formValue.billingContact,
        ...billingContact
      }
    };
  },
  async mounted() {
    await this.$nextTick();
    setAutoFocus(this.$el);
  },
  methods: {
    ...mapActions('app', [
      'editCompanyAndContacts'
    ]),
    handleFormStatusChange(status) {
      this.$set(this.formStatus, status.name, status.value);
    },
    async handleSaveClick() {
      const isFormValid = await this.$refs.form.validate();

      if (isFormValid) {
        await this.editCompanyAndContacts(this.formValue);
        this.$emit('submit');
      }
    },
    handleCancelClick() {
      this.$emit('cancel');
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@uikit/ui-kit/themes/acronis/src/common/variables';

.p-company-edit-form {
  .p-divider {
    height: $av-border-width-sm;
    background-color: var(--av-brand-lighter);
    margin-left: -24px;
    margin-right: -24px;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}
</style>
